import { useState, useEffect, useRef, Fragment } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { LimitUpgradeDialog } from '@/components/LimitUpgradeDialog';
import { SuperadminAICompanySelector } from '@/components/SuperadminAICompanySelector';
import { DEFAULT_FILTERS, filtersToParams } from '@/components/dashboard/DashboardFilters';
import { Bot, User, Phone, Send, UserCheck, X, MessageSquare, Clock, RefreshCw, PhoneCall, PhoneForwarded, Calendar, FileText, ArrowLeft, Loader2, Paperclip, Download, Image as ImageIcon, FolderOpen, AlertTriangle, Facebook, Instagram, Linkedin, MessageCircle, Music2 } from 'lucide-react';

function iconForMime(mime) {
  return (mime || '').startsWith('image/') ? ImageIcon : FileText;
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB'];
  let i = 0, v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}


function CanalPronto({ nombre, Icono, sub }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="canal-pronto">
      {Icono && <Icono className="w-10 h-10 text-white/10 mb-3" />}
      <p className="text-sm text-white/70">{nombre}</p>
      <p className="text-[11px] text-muted-foreground mt-1 max-w-xs">{sub || 'Canal preparado — se conectará próximamente.'}</p>
    </div>
  );
}

function WhatsAppIcon({ className }) {
  return (<svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm5.8 14.13c-.24.68-1.42 1.32-1.95 1.36-.5.04-.5.4-3.15-.66-2.65-1.06-4.32-3.79-4.45-3.97-.13-.18-1.06-1.41-1.06-2.69 0-1.28.67-1.91.91-2.17.24-.26.53-.33.7-.33.18 0 .35 0 .5.01.16.01.38-.06.59.45.24.58.81 2 .88 2.14.07.14.12.31.02.49-.09.18-.14.29-.28.45-.14.16-.29.36-.42.48-.14.13-.28.28-.12.55.16.27.71 1.17 1.53 1.9 1.05.94 1.94 1.23 2.21 1.37.27.14.43.12.59-.07.16-.18.68-.79.86-1.06.18-.27.36-.22.6-.13.24.09 1.55.73 1.82.86.27.13.45.2.51.31.07.11.07.63-.17 1.31z"/></svg>);
}

const CANALES_PRONTO = {
  ch_tiktok: { n: 'TikTok', I: Music2 },
  ch_linkedin: { n: 'LinkedIn', I: Linkedin },
};

function convChannel(c) {
  return c?.channel || 'whatsapp';
}
function convsForTab(convs, tab) {
  if (tab === 'conversations') return convs.filter((c) => convChannel(c) === 'whatsapp' || !c.channel);
  if (tab === 'ch_messenger') return convs.filter((c) => convChannel(c) === 'messenger');
  if (tab === 'ch_instagram') return convs.filter((c) => convChannel(c) === 'instagram' || convChannel(c) === 'instagram_comment');
  if (tab === 'ch_facebook') return convs.filter((c) => convChannel(c) === 'facebook_comment');
  return [];
}


function _callTipo(call) {
  return call.call_type === 'ai_bridge' ? 'IA Bridge'
    : call.call_type === 'voice_ia_inbound' ? 'IA entrante'
    : call.call_type === 'voice_ia_outbound' ? 'IA saliente'
    : call.call_type === 'bridge' ? 'Manual' : call.call_type;
}

function CallList({ calls, scheduled, selectedId, onSelect, onRefresh }) {
  const pend = (scheduled || []).filter(c => c.status === 'pending');
  return (
    <div className="space-y-1" data-testid="call-list">
      <div className="flex items-center justify-between px-2 py-2">
        <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Llamadas IA</p>
        <button onClick={onRefresh} className="text-muted-foreground hover:text-white" data-testid="refresh-calls-btn"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>
      {pend.length > 0 && (
        <div className="px-2 pb-1">
          <p className="text-[9px] text-amber-400/80 uppercase tracking-wider mb-1">Programadas</p>
          {pend.map(sc => (
            <div key={sc.id} className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/5 border border-amber-500/15 mb-1">
              <PhoneForwarded className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <div className="min-w-0"><p className="text-[11px] text-white truncate">{sc.lead_phone}</p><p className="text-[9px] text-muted-foreground">{new Date(sc.scheduled_at).toLocaleString('es-MX')}</p></div>
            </div>
          ))}
        </div>
      )}
      {(!calls || calls.length === 0) ? (
        <div className="text-center py-8"><PhoneCall className="w-8 h-8 mx-auto text-white/10 mb-2" /><p className="text-xs text-muted-foreground">No hay llamadas registradas</p></div>
      ) : (calls.map(call => {
        const an = call.call_analysis || {};
        const on = selectedId === call.id;
        return (
          <div key={call.id} onClick={() => onSelect(call.id)} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer ${on ? 'bg-blue-500/10 border border-blue-500/20' : 'hover:bg-white/[0.03]'}`} data-testid={`call-row-${call.id}`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${call.status === 'completed' ? 'bg-emerald-500/10' : call.status === 'initiated' ? 'bg-blue-500/10' : 'bg-white/5'}`}><PhoneCall className={`w-4 h-4 ${call.status === 'completed' ? 'text-emerald-400' : call.status === 'initiated' ? 'text-blue-400' : 'text-muted-foreground'}`} /></div>
            <div className="min-w-0 flex-1"><p className="text-xs text-white font-medium truncate">{call.lead_name || call.lead_phone}</p><p className="text-[10px] text-muted-foreground truncate">{_callTipo(call)} · {new Date(call.created_at).toLocaleDateString('es-MX')}{(call.transcript_text || an.summary) ? ' · transcrita' : ''}</p></div>
            {typeof an.sales_skill_score === 'number' && <span className="text-[10px] text-amber-300 shrink-0">{an.sales_skill_score}</span>}
          </div>
        );
      }))}
    </div>
  );
}

function CallDetail({ call, onBack }) {
  if (!call) return (<div className="flex-1 flex items-center justify-center text-center p-8"><div><PhoneCall className="w-10 h-10 mx-auto text-white/10 mb-3" /><p className="text-sm text-muted-foreground">Selecciona una llamada para ver su transcripción</p></div></div>);
  const an = call.call_analysis || {};
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="p-3 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {onBack && <button onClick={onBack} className="lg:hidden text-muted-foreground mr-1" data-testid="call-back"><ArrowLeft className="w-4 h-4" /></button>}
          <div className="min-w-0"><p className="text-sm text-white font-medium truncate">{call.lead_name || call.lead_phone}</p><p className="text-[10px] text-muted-foreground">{_callTipo(call)} · {new Date(call.created_at).toLocaleString('es-MX')}</p></div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {call.recording_url && <a href={call.recording_url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-400 hover:underline">Grabación</a>}
          <Badge variant="outline" className={`text-[10px] border-white/10 ${call.status === 'completed' ? 'text-emerald-400' : call.status === 'initiated' ? 'text-blue-400' : 'text-muted-foreground'}`}>{call.status === 'completed' ? 'Completada' : call.status === 'initiated' ? 'En curso' : call.status}</Badge>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {(an.interest_level || typeof an.sales_skill_score === 'number') && (
          <div className="flex flex-wrap gap-2 text-[10px]">
            {an.interest_level && <span className="px-2 py-0.5 rounded bg-white/5 text-white/80">Interés: {an.interest_level}</span>}
            {typeof an.sales_skill_score === 'number' && <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-300">Eficiencia IA: {an.sales_skill_score}/10</span>}
          </div>
        )}
        {an.summary && (<div><p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">Resumen</p><p className="text-xs text-white/85 whitespace-pre-wrap">{an.summary}</p></div>)}
        <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">Transcripción</p>{call.transcript_text ? <p className="text-xs text-white/70 whitespace-pre-wrap leading-relaxed">{call.transcript_text}</p> : <p className="text-[11px] text-muted-foreground">Sin transcripción todavía (se genera al terminar y grabar la llamada).</p>}</div>
      </div>
    </div>
  );
}


function StatusBadge({ status, needsTakeover }) {
  if (status === 'taken_over') return <Badge className="text-[9px] bg-blue-500/10 text-blue-400 border-blue-500/20">Humano</Badge>;
  if (status === 'closed') return <Badge className="text-[9px] bg-white/5 text-muted-foreground border-white/10">Cerrada</Badge>;
  if (needsTakeover) return <Badge className="text-[9px] bg-amber-500/10 text-amber-400 border-amber-500/20 animate-pulse">Atencion</Badge>;
  return <Badge className="text-[9px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">IA Activa</Badge>;
}

function ConversationList({ conversations, selected, onSelect, onRefresh }) {
  return (
    <div className="space-y-1" data-testid="conversation-list">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Conversaciones</p>
        <Button variant="ghost" size="sm" onClick={onRefresh} className="h-7 w-7 p-0 text-muted-foreground" data-testid="refresh-conversations">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>
      {conversations.length === 0 && (
        <div className="text-center py-8">
          <Bot className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-xs text-muted-foreground">No hay conversaciones activas</p>
          <p className="text-[10px] text-muted-foreground/60 mt-1">Las conversaciones apareceran cuando el agente IA reciba mensajes</p>
        </div>
      )}
      {conversations.map(conv => (
        <button
          key={conv.id}
          onClick={() => onSelect(conv.id)}
          data-testid={`conv-item-${conv.id}`}
          className={`w-full text-left p-3 rounded-lg border transition-all duration-200 ${
            selected === conv.id
              ? 'bg-primary/10 border-primary/30'
              : 'bg-black/20 border-white/5 hover:border-white/10 hover:bg-white/[0.02]'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-white font-medium truncate">{conv.lead_name || conv.phone}</span>
            <StatusBadge status={conv.status} needsTakeover={conv.needs_takeover} />
          </div>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <Phone className="w-3 h-3" />
            <span>{conv.phone}</span>
            <span className="ml-auto">{conv.messages?.length || 0} msgs</span>
          </div>
          {conv.messages?.length > 0 && (
            <p className="text-[11px] text-muted-foreground/80 mt-1 truncate">
              {conv.messages[conv.messages.length - 1]?.content?.substring(0, 60)}...
            </p>
          )}
        </button>
      ))}
    </div>
  );
}

// Separador de día estilo WhatsApp: calcula todo en hora LOCAL del navegador (new Date),
// nunca con substring del ISO (evita desfase de día por UTC).
const sameLocalDay = (a, b) => {
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
};
const formatDaySeparator = (iso) => {
  const d = new Date(iso), now = new Date();
  const yesterday = new Date(); yesterday.setDate(now.getDate() - 1);
  if (sameLocalDay(d, now)) return 'Hoy';
  if (sameLocalDay(d, yesterday)) return 'Ayer';
  return d.toLocaleDateString('es-MX', {
    weekday: 'short', day: 'numeric', month: 'short',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {})
  });
};

function ChatView({ conversation, onTakeover, onHandoffToIa, onSend, onClose, onRefresh, onBridgeCall, onSendPdf, onAttachFiles, sending, onBack, bridgeCallLoading }) {
  const messagesEndRef = useRef(null);
  // Fix auto-scroll (2026-10-03): se mueve SOLO el contenedor de mensajes (no la pagina),
  // y solo al abrir/cambiar de conversacion o cuando llega un mensaje nuevo estando abajo.
  const scrollBoxRef = useRef(null);
  const nearBottomRef = useRef(true);
  const lastConvIdRef = useRef(null);
  const lastMsgKeyRef = useRef('');
  const [message, setMessage] = useState('');
  const [attachOpen, setAttachOpen] = useState(false);
  const [libraryFiles, setLibraryFiles] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [sendingFiles, setSendingFiles] = useState(false);

  const openAttachModal = async () => {
    setAttachOpen(true);
    setSelectedFileIds([]);
    setLibraryLoading(true);
    try {
      const r = await api.get('/file-library', { params: { limit: 100 } });
      setLibraryFiles(r.data.files || []);
    } catch {
      toast.error('Error al cargar la Biblioteca');
    } finally {
      setLibraryLoading(false);
    }
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFileIds(prev => prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]);
  };

  const handleSendSelected = async () => {
    setSendingFiles(true);
    try {
      await onAttachFiles(conversation, selectedFileIds);
      setAttachOpen(false);
    } finally {
      setSendingFiles(false);
    }
  };

  const handleDownloadFile = async (fileId, originalName) => {
    try {
      const r = await api.get(`/files/${fileId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = originalName || 'archivo';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo descargar el archivo (puede haber expirado)');
    }
  };

  const convId = conversation?.id;
  const msgList = conversation?.messages || [];
  const lastMsg = msgList[msgList.length - 1];
  const lastMsgKey = `${msgList.length}|${lastMsg?.timestamp || ''}|${lastMsg?.role || ''}`;

  useEffect(() => {
    const box = scrollBoxRef.current;
    if (!box || !convId) return;
    const convChanged = convId !== lastConvIdRef.current;
    const newMessage = lastMsgKey !== lastMsgKeyRef.current;
    lastConvIdRef.current = convId;
    lastMsgKeyRef.current = lastMsgKey;
    if (convChanged) {
      box.scrollTop = box.scrollHeight;
      nearBottomRef.current = true;
    } else if (newMessage && nearBottomRef.current) {
      box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
    }
  }, [convId, lastMsgKey]);

  const handleMessagesScroll = (e) => {
    const el = e.currentTarget;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  if (!conversation) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Bot className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
          <p className="text-sm text-muted-foreground">Selecciona una conversacion</p>
        </div>
      </div>
    );
  }

  const handleSend = () => {
    if (!message.trim()) return;
    nearBottomRef.current = true; // tras enviar, bajar al mensaje propio
    onSend(message);
    setMessage('');
  };

  const isActive = conversation.status === 'active';
  const isTakenOver = conversation.status === 'taken_over';
  const isClosed = conversation.status === 'closed';

  return (
    <div className="flex flex-col h-full" data-testid="chat-view">
      <div className="p-4 border-b border-white/5 flex items-center justify-between">
        <div>
          {onBack && <Button variant="ghost" size="sm" onClick={onBack} className="h-7 px-2 mb-2 text-muted-foreground lg:hidden" data-testid="mobile-back-conversations"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Volver</Button>}
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            {conversation.lead_name || conversation.phone}
            <StatusBadge status={conversation.status} needsTakeover={conversation.needs_takeover} />
          </h3>
          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Phone className="w-3 h-3" /> {conversation.phone}
            {conversation.lead_email && <> | {conversation.lead_email}</>}
          </p>
          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
            <UserCheck className="w-3 h-3" /> Asesor: {conversation.assigned_vendor_name || 'Sin asesor'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onRefresh} className="h-8 text-xs text-muted-foreground" data-testid="refresh-chat">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          {isActive && (
            <Button onClick={onTakeover} size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-500" data-testid="takeover-btn">
              <UserCheck className="w-3.5 h-3.5 mr-1" /> Tomar Control
            </Button>
          )}
          {isTakenOver && (
            <Button onClick={onHandoffToIa} size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-500" data-testid="handoff-to-ia-btn">
              <Bot className="w-3.5 h-3.5 mr-1" /> Regresar a IA
            </Button>
          )}
          {(isActive || isTakenOver) && conversation.phone && (
            <Button onClick={() => onBridgeCall(conversation)} disabled={bridgeCallLoading} size="sm" variant="outline" className="h-8 text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" data-testid="bridge-call-btn">
              <PhoneCall className="w-3.5 h-3.5 mr-1" /> {bridgeCallLoading ? 'Llamando…' : 'Llamar'}
            </Button>
          )}
          {(isActive || isTakenOver) && (
            <Button onClick={openAttachModal} disabled={sending} size="sm" variant="outline" className="h-8 text-xs border-white/10 text-muted-foreground hover:bg-white/5" data-testid="attach-file-btn">
              <Paperclip className="w-3.5 h-3.5 mr-1" /> Adjuntar
            </Button>
          )}
          {!isClosed && (
            <Button onClick={onClose} variant="outline" size="sm" className="h-8 text-xs border-white/10 text-muted-foreground" data-testid="close-conv-btn">
              <X className="w-3.5 h-3.5 mr-1" /> Cerrar
            </Button>
          )}
        </div>
      </div>

      <div ref={scrollBoxRef} onScroll={handleMessagesScroll} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {conversation.messages?.map((msg, i) => {
          const prev = conversation.messages[i - 1];
          const showSeparator = msg.timestamp && (i === 0 || !prev?.timestamp || !sameLocalDay(msg.timestamp, prev.timestamp));
          return (
          <Fragment key={i}>
            {showSeparator && (
              <div className="flex justify-center">
                <span className="text-[10px] text-muted-foreground bg-white/5 border border-white/10 rounded-full px-3 py-1">
                  {formatDaySeparator(msg.timestamp)}
                </span>
              </div>
            )}
          <div className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
              msg.role === 'user'
                ? 'bg-white/5 border border-white/10 rounded-tl-sm'
                : msg.role === 'human_agent'
                  ? 'bg-blue-600/20 border border-blue-500/20 rounded-tr-sm'
                  : 'bg-primary/10 border border-primary/20 rounded-tr-sm'
            }`}>
              <div className="flex items-center gap-1.5 mb-1">
                {msg.role === 'user' ? <User className="w-3 h-3 text-muted-foreground" /> : msg.role === 'human_agent' ? <UserCheck className="w-3 h-3 text-blue-400" /> : <Bot className="w-3 h-3 text-primary" />}
                <span className="text-[9px] text-muted-foreground uppercase tracking-wider">
                  {msg.role === 'user' ? 'Cliente' : msg.role === 'human_agent' ? (msg.sent_by || 'Agente') : 'IA'}
                </span>
                <span className="text-[9px] text-muted-foreground/50 ml-auto">
                  {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
              <p className="text-sm text-white/90 whitespace-pre-wrap">{msg.content}</p>
              {msg.files?.map((f) => (
                f.expired ? (
                  <div key={f.file_id} className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground italic">
                    <FileText className="w-3.5 h-3.5" /> Archivo expirado
                  </div>
                ) : (
                  <button
                    key={f.file_id}
                    onClick={() => handleDownloadFile(f.file_id, f.original_name)}
                    className="mt-1.5 flex items-center gap-1.5 text-[11px] text-primary hover:underline"
                    data-testid="message-file-download"
                  >
                    <Download className="w-3.5 h-3.5" /> {f.original_name} <span className="text-muted-foreground">({formatFileSize(f.size_bytes)})</span>
                  </button>
                )
              ))}
            </div>
          </div>
          </Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {isTakenOver && (
        <div className="p-4 border-t border-white/5">
          <div className="flex gap-2">
            <Input
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Escribe un mensaje..."
              className="bg-black/20 border-white/10 focus:border-primary"
              data-testid="chat-input"
            />
            <Button onClick={handleSend} disabled={!message.trim() || sending} className="bg-primary hover:bg-primary/90 shrink-0" data-testid="send-message-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Estas respondiendo como agente humano</p>
        </div>
      )}
      {isActive && (
        <div className="p-4 border-t border-white/5 bg-primary/[0.03]">
          <p className="text-xs text-primary/80 flex items-center gap-2">
            <Bot className="w-4 h-4" />
            El agente IA esta manejando esta conversacion. Presiona «Tomar Control» para intervenir.
          </p>
        </div>
      )}
      {isClosed && (
        <div className="p-4 border-t border-white/5 bg-white/[0.02]">
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Conversacion cerrada {conversation.closed_by ? `por ${conversation.closed_by}` : ''}
          </p>
        </div>
      )}

      <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
        <DialogContent className="max-w-md bg-[#1F2833] border-white/10" data-testid="attach-file-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-primary" /> Adjuntar de la Biblioteca
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Elige uno o varios archivos autorizados para enviarlos a {conversation.lead_name || conversation.phone} por WhatsApp.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1.5">
            {libraryLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : libraryFiles.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                No tienes archivos autorizados en la Biblioteca todavia.
              </p>
            ) : libraryFiles.map(f => {
              const Icon = iconForMime(f.mime_type);
              const checked = selectedFileIds.includes(f.file_id);
              return (
                // div (no <button>) a propósito: adentro va un Checkbox de Radix, que YA es un
                // <button role="checkbox"> — anidar button-en-button es HTML inválido.
                <div
                  key={f.file_id}
                  role="button"
                  tabIndex={sendingFiles ? -1 : 0}
                  onClick={() => !sendingFiles && toggleFileSelection(f.file_id)}
                  onKeyDown={(e) => { if (!sendingFiles && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggleFileSelection(f.file_id); } }}
                  aria-disabled={sendingFiles}
                  className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg border text-left transition-colors cursor-pointer ${
                    sendingFiles ? 'opacity-50 pointer-events-none' : ''
                  } ${checked ? 'bg-primary/10 border-primary/40' : 'bg-black/20 border-white/5 hover:border-primary/30 hover:bg-primary/5'}`}
                  data-testid={`attach-file-option-${f.file_id}`}
                >
                  <Checkbox checked={checked} className="shrink-0 pointer-events-none" />
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-white truncate">{f.original_name}</p>
                    <p className="text-[10px] text-muted-foreground">{formatFileSize(f.size_bytes)}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <span className="text-xs text-muted-foreground">
              {selectedFileIds.length} archivo{selectedFileIds.length === 1 ? '' : 's'} seleccionado{selectedFileIds.length === 1 ? '' : 's'}
            </span>
            <Button
              onClick={handleSendSelected}
              disabled={selectedFileIds.length === 0 || sendingFiles}
              size="sm"
              className="h-8 text-xs"
              data-testid="attach-file-send-btn"
            >
              {sendingFiles ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Paperclip className="w-3.5 h-3.5 mr-1" />}
              Enviar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AIAgentPage() {
  const { user, impersonating } = useAuth();
  const isSuperadminNoTenant = !impersonating
    && user?.role === 'superadmin'
    && (user?.company_id === 'dealider-system' || user?.platform_role === 'owner' || user?.platform_role === 'superadmin');
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [filterSearch, setFilterSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Mismos filtros dimensionales que el Dashboard. Default: últimos 30 días.
  const buildLast30Filters = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { ...DEFAULT_FILTERS, date_from: fmt(from), date_to: fmt(to) };
  };
  const [dashFilters, setDashFilters] = useState(() => buildLast30Filters());
  // Espejo de los filtros vivos: el polling de 5s captura loadConversations del primer
  // render (closure), así que lee los params desde este ref para no quedarse con filtros stale.
  const filtersRef = useRef({ search: '', ...buildLast30Filters() });
  const [selectedId, setSelectedId] = useState(searchParams.get('conv') || null);
  // Espejo de selectedId para el polling de 5s (closure con deps []); mismo patron que filtersRef.
  const selectedIdRef = useRef(searchParams.get('conv') || null);
  const [selectedConv, setSelectedConv] = useState(null);
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);
  const [bridgeCallLoading, setBridgeCallLoading] = useState(false);
  const [voiceCalls, setVoiceCalls] = useState([]);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('conversations');
  const [tiktokConn, setTiktokConn] = useState(null);
  useEffect(() => { api.get('/integrations/tiktok/status').then((r) => setTiktokConn((r && r.data) || {})).catch(() => setTiktokConn({})); }, []);
  const [licenseHealth, setLicenseHealth] = useState(null);
  const [aiLimitDialogOpen, setAiLimitDialogOpen] = useState(false);
  const pollRef = useRef(null);
  // Ultima respuesta de la conversacion abierta: si el polling trae lo mismo, no se re-renderiza.
  const convSnapRef = useRef({ id: null, json: '', fp: null });



  const loadConversations = async () => {
    try {
      // Lee los filtros vivos del ref (el polling captura este closure del 1er render).
      const f = filtersRef.current;
      const params = filtersToParams(f);
      if (f.search?.trim()) params.search = f.search.trim();
      // Compat backend: seller_id también como assigned_to
      if (params.seller_id) params.assigned_to = params.seller_id;
      const r = await api.get('/ai-agent/conversations', { params });
      setConversations(r.data);
    } catch { /* noop */ }
  };

  const loadStats = async () => {
    try {
      const f = filtersRef.current;
      const params = filtersToParams(f);
      if (params.seller_id) params.assigned_to = params.seller_id;
      const r = await api.get('/ai-agent/stats', { params });
      setStats(r.data);
    } catch { /* noop */ }
  };

  const loadConversation = async (id) => {
    try {
      const snap = convSnapRef.current;
      const params = (snap.id === id && snap.fp) ? { known_fp: snap.fp } : undefined;
      const r = await api.get(`/ai-agent/conversations/${id}`, params ? { params } : undefined);
      // Respuesta tardia de una conversacion que ya no esta abierta: se descarta.
      if (id !== selectedIdRef.current) return;
      const data = r.data || {};
      if (data.unchanged && snap.id === id) return; // backend: nada cambio
      const json = JSON.stringify(data);
      const cur = convSnapRef.current;
      if (cur.id === id && cur.json === json) return; // identica: no re-render
      convSnapRef.current = { id, json, fp: data.sync_fp || null };
      setSelectedConv(data);
    } catch { toast.error('Error al cargar conversacion'); }
  };

  const loadVoiceCalls = async () => {
    try {
      const [vc, sc] = await Promise.all([
        api.get('/voice/calls').catch(() => ({ data: { calls: [] } })),
        api.get('/voice/scheduled-calls').catch(() => ({ data: { calls: [] } })),
      ]);
      setVoiceCalls(vc.data?.calls || []);
      setScheduledCalls(sc.data?.calls || []);
    } catch { /* noop */ }
  };

  const loadLicenseHealth = async () => {
    try {
      const r = await api.get('/subscription/license-health');
      setLicenseHealth(r.data);
      if (r.data?.limits?.ai_tokens?.exceeded) {
        setAiLimitDialogOpen(true);
      }
    } catch { /* noop */ }
  };


  // Debounce del buscador: filterSearch (input) -> debouncedSearch (dispara el fetch).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filterSearch), 350);
    return () => clearTimeout(t);
  }, [filterSearch]);

  // Al cambiar un filtro: sincroniza el ref (para el polling) y refresca YA (no espera 5s).
  useEffect(() => {
    filtersRef.current = { search: debouncedSearch, ...dashFilters };
    loadConversations();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, dashFilters]);

  useEffect(() => {
    Promise.all([loadConversations(), loadStats(), loadVoiceCalls(), loadLicenseHealth()]).finally(() => setLoading(false));

    const startPolling = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => {
        loadConversations();
        if (selectedIdRef.current) loadConversation(selectedIdRef.current);
        loadLicenseHealth();
      }, 5000);
    };

    const stopPolling = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    if (!document.hidden) startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    selectedIdRef.current = selectedId;
    if (selectedId) loadConversation(selectedId);
  }, [selectedId]);


  const handleTakeover = async () => {
    try {
      await api.post(`/ai-agent/takeover/${selectedId}`);
      toast.success('Has tomado el control de la conversacion');
      loadConversation(selectedId);
      loadConversations();
      loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleHandoffToIa = async () => {
    try {
      await api.post(`/ai-agent/handoff-to-ia/${selectedId}`);
      toast.success('Conversación devuelta al agente IA');
      loadConversation(selectedId);
      loadConversations();
      loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleSend = async (message) => {
    setSending(true);
    try {
      await api.post(`/ai-agent/send/${selectedId}`, { message });
      loadConversation(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al enviar'); }
    finally { setSending(false); }
  };

  const handleClose = async () => {
    try {
      await api.post(`/ai-agent/close/${selectedId}`);
      toast.success('Conversacion cerrada');
      loadConversation(selectedId);
      loadConversations();
      loadStats();
    } catch { toast.error('Error'); }
  };

  const handleBridgeCall = async (conv) => {
    if (bridgeCallLoading) return;
    setBridgeCallLoading(true);
    try {
      const r = await api.post('/voice/bridge-call', {
        conversation_id: conv.id,
      });
      if (r.data.success) {
        toast.success('Llamada iniciada. Ambos recibiran la llamada en breve.');
        loadVoiceCalls();
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Error al iniciar llamada';
      if (msg.includes('no configurado')) {
        toast.error('Twilio no configurado. Pide al Super Admin que lo configure en la seccion Plataforma.');
      } else {
        toast.error(msg);
      }
    } finally {
      setBridgeCallLoading(false);
    }
  };

  const handleSendPdf = async (conv) => {
    try {
      await api.post(`/ai-agent/send-property-pdf/${conv.id}`);
      toast.success('Ficha enviada al lead');
      loadConversation(conv.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar la ficha');
    }
  };

  const handleAttachMultipleFiles = async (conv, fileIds) => {
    setSending(true);
    try {
      const r = await api.post(`/file-library/send-multiple-to-conversation/${conv.id}`, { file_ids: fileIds });
      const { sent, pending, failed, details } = r.data;
      if (failed > 0) {
        const rejectedNames = (details || []).filter(d => d.status === 'rejected').map(d => d.file_name || d.file_id).join(', ');
        toast.warning(`${sent} enviado(s), ${pending} pendiente(s), ${failed} rechazado(s)${rejectedNames ? `: ${rejectedNames}` : ''}`);
      } else if (pending > 0) {
        toast.info(`${sent} enviado(s), ${pending} pendiente(s) — ventana cerrada, se entregarán cuando el lead responda.`);
      } else {
        toast.success(`${sent} archivo${sent === 1 ? '' : 's'} enviado${sent === 1 ? '' : 's'} al lead`);
      }
      loadConversation(conv.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron enviar los archivos');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const aiTokenState = licenseHealth?.limits?.ai_tokens;
  const isAiTokenLimitReached = !!aiTokenState?.exceeded;

  return (
    <div className="h-[calc(100vh-2rem)] sm:h-[calc(100vh-3rem)] lg:h-[calc(100vh-4rem)] flex flex-col overflow-hidden" data-testid="ai-agent-page">
      <div className="mb-2 flex-shrink-0">
        <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
          <Bot className="w-5 h-5 text-primary" />
          Conversaciones IA
        </h2>
        {isAiTokenLimitReached && (
          <div className="mt-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2" data-testid="ai-token-limit-warning">
            <p className="text-[11px] text-red-300">Límite de tokens IA alcanzado ({aiTokenState?.current}/{aiTokenState?.limit}).</p>
          </div>
        )}
      </div>

      {isSuperadminNoTenant ? (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <SuperadminAICompanySelector targetLabel="Agente IA" />
        </div>
      ) : (
      <>
      <div className="flex-shrink-0 mb-2 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="agent-topbar">
        <div className="flex items-center gap-4">
          <div className="text-center leading-none"><p className="text-lg font-bold text-white">{stats?.total_conversations ?? '—'}</p><p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">Convs</p></div>
          <div className="text-center leading-none"><p className="text-lg font-bold text-blue-400">{stats ? `${stats.conversion_rate}%` : '—'}</p><p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">Conversión</p></div>
        </div>
        {user?.role !== 'vendor' && (
          <div className="flex items-center gap-1.5">
            <Input type="date" value={dashFilters.date_from || ''} onChange={e => setDashFilters({ ...dashFilters, date_from: e.target.value })} className="h-8 text-xs bg-black/20 border-white/10 w-32" data-testid="conv-date-from" />
            <span className="text-muted-foreground text-xs">–</span>
            <Input type="date" value={dashFilters.date_to || ''} onChange={e => setDashFilters({ ...dashFilters, date_to: e.target.value })} className="h-8 text-xs bg-black/20 border-white/10 w-32" data-testid="conv-date-to" />
            <Button variant="ghost" size="sm" className="h-8 text-[10px] text-muted-foreground px-2" onClick={() => setDashFilters(buildLast30Filters())} data-testid="conv-date-reset">30d</Button>
          </div>
        )}
        <Input type="text" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Buscar por nombre o teléfono…" className="h-8 text-xs bg-black/20 border-white/10 flex-1 min-w-[180px] max-w-xs" data-testid="conv-filter-search" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex-1 flex flex-col min-h-0">
        <TabsList className="bg-black/30 border border-white/5 mb-2 flex h-auto gap-0.5 p-1 flex-shrink-0 w-fit" data-testid="agent-tabs">
          <TabsTrigger value="conversations" title="WhatsApp" className="relative data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400 text-emerald-400/70 p-2">
            <WhatsAppIcon className="w-4 h-4" />
            {conversations.filter(c => c.status === 'active' && c.needs_takeover).length > 0 && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-500 text-[8px] text-white flex items-center justify-center animate-pulse">{conversations.filter(c => c.status === 'active' && c.needs_takeover).length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="calls" title="Llamadas IA (transcritas)" className="relative data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 text-blue-400/70 p-2" data-testid="agent-tab-calls">
            <PhoneCall className="w-4 h-4" />
            {voiceCalls.filter(c => c.status === 'initiated').length > 0 && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-blue-500 text-[8px] text-white flex items-center justify-center">{voiceCalls.filter(c => c.status === 'initiated').length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="ch_messenger" title="Messenger" className="relative data-[state=active]:bg-sky-500/20 data-[state=active]:text-sky-400 text-sky-400/70 p-2" data-testid="agent-tab-messenger"><MessageCircle className="w-4 h-4" /></TabsTrigger>
          <TabsTrigger value="ch_instagram" title="Instagram (mensajes y muro)" className="relative data-[state=active]:bg-fuchsia-500/20 data-[state=active]:text-fuchsia-400 text-fuchsia-400/70 p-2" data-testid="agent-tab-instagram"><Instagram className="w-4 h-4" /></TabsTrigger>
          <TabsTrigger value="ch_facebook" title="Facebook (muro)" className="relative data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 text-blue-400/70 p-2" data-testid="agent-tab-facebook"><Facebook className="w-4 h-4" /></TabsTrigger>
          <TabsTrigger value="ch_tiktok" title={tiktokConn && tiktokConn.connected ? 'TikTok — conectado' : 'TikTok — próximamente'} className={tiktokConn && tiktokConn.connected ? 'p-2 text-fuchsia-400/70' : 'p-2 text-muted-foreground/40'} data-testid="agent-tab-tiktok"><Music2 className="w-4 h-4" /></TabsTrigger>
          <TabsTrigger value="ch_linkedin" title="LinkedIn — próximamente" className="p-2 text-muted-foreground/40" data-testid="agent-tab-linkedin"><Linkedin className="w-4 h-4" /></TabsTrigger>
        </TabsList>

        <div className="flex-1 flex flex-col min-h-0 mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-full min-h-0 overflow-hidden">
            <div className={`${(selectedId || selectedCallId) ? 'hidden lg:block' : 'block'} lg:col-span-4 overflow-y-auto pr-2 min-h-0`}>
              {(activeTab === 'conversations' || activeTab === 'ch_messenger' || activeTab === 'ch_instagram' || activeTab === 'ch_facebook') && (
                <ConversationList conversations={convsForTab(conversations, activeTab)} selected={selectedId} onSelect={setSelectedId} onRefresh={loadConversations} />
              )}
              {activeTab === 'calls' && (
                <CallList calls={voiceCalls} scheduled={scheduledCalls} selectedId={selectedCallId} onSelect={setSelectedCallId} onRefresh={loadVoiceCalls} />
              )}
              {CANALES_PRONTO[activeTab] && (
                <div className="text-center py-10 text-muted-foreground text-xs" data-testid="canal-lista-vacia">Sin conversaciones en este canal todavía.</div>
              )}
            </div>
            <div className={`${(selectedId || selectedCallId) ? 'block' : 'hidden lg:block'} lg:col-span-8 min-h-0`}>
              <Card className="bg-card/50 backdrop-blur-md border-white/5 h-full flex flex-col overflow-hidden">
                {(activeTab === 'conversations' || activeTab === 'ch_messenger' || activeTab === 'ch_instagram' || activeTab === 'ch_facebook') && (
                  <ChatView conversation={selectedConv} onTakeover={handleTakeover} onHandoffToIa={handleHandoffToIa} onSend={handleSend} onSendPdf={handleSendPdf} onAttachFiles={handleAttachMultipleFiles} onClose={handleClose} onBridgeCall={handleBridgeCall} onBack={() => setSelectedId(null)} onRefresh={() => selectedId && loadConversation(selectedId)} sending={sending} bridgeCallLoading={bridgeCallLoading} />
                )}
                {activeTab === 'calls' && (
                  <CallDetail call={voiceCalls.find(c => c.id === selectedCallId)} onBack={() => setSelectedCallId(null)} />
                )}
                {CANALES_PRONTO[activeTab] && (
                  <CanalPronto nombre={CANALES_PRONTO[activeTab].n} Icono={CANALES_PRONTO[activeTab].I} sub={activeTab === 'ch_tiktok' ? (tiktokConn && tiktokConn.connected ? ('TikTok conectado como ' + (tiktokConn.account || 'tu cuenta') + '. La IA atenderá tus DMs en cuanto TikTok apruebe a LeadCEM.') : 'Conéctalo en la sección Redes Sociales.') : undefined} />
                )}
              </Card>
            </div>
          </div>
        </div>
      </Tabs>
      </>
      )}

      <LimitUpgradeDialog
        open={aiLimitDialogOpen}
        onOpenChange={setAiLimitDialogOpen}
        title="Límite de tokens IA alcanzado"
        description="Tu automatización IA se pausará hasta ampliar el plan o el límite mensual."
        current={Number(aiTokenState?.current || 0)}
        limit={Number(aiTokenState?.limit || 0)}
        testIdPrefix="ai-token-limit"
      />
    </div>
  );
}
